/**
 * Scent finder on the Digital Lab page.
 * Each radio value lists "handle:points" pairs. On submit the points are tallied and the
 * matching pre-rendered [data-quiz-result] card is revealed; ties go to the experiment that
 * appears first in the form's data-order list.
 */
const quiz = document.querySelector('[data-scent-quiz]');
const results = document.querySelector('[data-quiz-results]');

if (quiz && results) {
  const order = (quiz.dataset.order || '').split(',').filter(Boolean);
  const cards = [...results.querySelectorAll('[data-quiz-result]')];
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const tally = () => {
    const scores = new Map();
    for (const input of quiz.querySelectorAll('input[type="radio"]:checked')) {
      for (const pair of input.value.split(',')) {
        const [handle, points] = pair.split(':');
        if (!handle) continue;
        scores.set(handle, (scores.get(handle) || 0) + Number(points || 1));
      }
    }
    let best = null;
    let bestScore = -1;
    for (const handle of order) {
      const score = scores.get(handle) || 0;
      if (score > bestScore) {
        best = handle;
        bestScore = score;
      }
    }
    return best;
  };

  quiz.addEventListener('submit', (event) => {
    event.preventDefault();
    const match = tally();
    const card = cards.find((item) => item.dataset.quizResult === match);
    if (!card) return;
    for (const item of cards) item.hidden = item !== card;
    card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    card.querySelector('[data-quiz-result-heading]')?.focus({ preventScroll: true });
  });

  results.addEventListener('click', (event) => {
    if (!event.target.closest('[data-quiz-retake]')) return;
    for (const item of cards) item.hidden = true;
    quiz.reset();
    quiz.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    quiz.querySelector('input[type="radio"]')?.focus({ preventScroll: true });
  });
}

/**
 * Share-to-unlock reward under the revealed experiment ([data-quiz-share]).
 * Any share (Facebook, X, WhatsApp, the device share sheet, or copying the link) reveals the discount code; the
 * unlock is remembered in localStorage. The "shop" link applies the code through /discount/CODE and returns to
 * the revealed product.
 */
const sharePanel = document.querySelector('[data-quiz-share]');

if (quiz && results && sharePanel) {
  const reward = sharePanel.querySelector('[data-share-reward]');
  const status = sharePanel.querySelector('[data-share-status]');
  const shopLink = sharePanel.querySelector('[data-reward-shop]');
  const nativeButton = sharePanel.querySelector('[data-share="native"]');
  const code = sharePanel.dataset.shareCode || '';
  const storageKey = `newdna-share-reward:${code}`;
  let current = null;

  if (nativeButton && typeof navigator.share === 'function') nativeButton.hidden = false;

  const remembered = () => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  };

  const unlock = (message) => {
    reward.hidden = false;
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* Private mode: the reward still shows for this visit. */
    }
    if (message) status.textContent = message;
  };

  const show = (card) => {
    current = {
      title: card.dataset.shareTitle || '',
      url: card.dataset.shareUrl || location.href,
      path: card.dataset.sharePath || '/',
    };
    current.text = (sharePanel.dataset.shareText || '').replace('[experiment]', current.title);
    shopLink.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(current.path)}`;
    sharePanel.hidden = false;
    reward.hidden = !remembered();
  };

  const openShare = (href) => {
    window.open(href, '_blank', 'noopener,noreferrer,width=640,height=560');
  };

  quiz.addEventListener('submit', () => {
    // Runs after the reveal handler above has unhidden the matching card.
    queueMicrotask(() => {
      const card = results.querySelector('[data-quiz-result]:not([hidden])');
      if (card) show(card);
    });
  });

  results.addEventListener('click', (event) => {
    if (event.target.closest('[data-quiz-retake]')) sharePanel.hidden = true;
  });

  sharePanel.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share], [data-reward-copy]');
    if (!button || !current) return;

    if (button.hasAttribute('data-reward-copy')) {
      try {
        await navigator.clipboard.writeText(code);
        status.textContent = sharePanel.dataset.msgCodeCopied || '';
      } catch {
        /* Clipboard blocked: the code stays visible to copy by hand. */
      }
      return;
    }

    const url = encodeURIComponent(current.url);
    const text = encodeURIComponent(current.text);
    switch (button.dataset.share) {
      case 'facebook':
        openShare(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
        unlock(sharePanel.dataset.msgUnlocked);
        break;
      case 'x':
        openShare(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
        unlock(sharePanel.dataset.msgUnlocked);
        break;
      case 'whatsapp':
        openShare(`https://wa.me/?text=${text}%20${url}`);
        unlock(sharePanel.dataset.msgUnlocked);
        break;
      case 'native':
        try {
          await navigator.share({ title: current.title, text: current.text, url: current.url });
          unlock(sharePanel.dataset.msgUnlocked);
        } catch {
          /* Cancelled: no reward. */
        }
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${current.text} ${current.url}`);
          unlock(sharePanel.dataset.msgLinkCopied);
        } catch {
          /* Clipboard blocked. */
        }
        break;
    }
  });
}
